import { useRef } from 'react';
import { Button } from '@/components/ui/Button';
import type { TbmReport } from './api';

interface TbmReportPdfModalProps {
  open: boolean;
  onClose: () => void;
  data: TbmReport;
  isPelita?: boolean;
}

const DEFAULT_TBM_BANDS = [
  { name: 'Berdampak', range: '86 – 100', desc: 'TBM menjadi pusat belajar dan pemberdayaan masyarakat yang berkelanjutan serta memberi manfaat luas.' },
  { name: 'Mandiri', range: '71 – 85', desc: 'TBM mampu mengelola program secara mandiri, membangun kolaborasi, dan menghasilkan dampak yang terukur.' },
  { name: 'Berkembang', range: '51 – 70', desc: 'TBM mampu mengelola program secara terencana, rutin, dan mulai membangun jejaring serta partisipasi masyarakat.' },
  { name: 'Bertumbuh', range: '26 – 50', desc: 'TBM mulai memiliki sistem pengelolaan dan program literasi, namun belum konsisten.' },
  { name: 'Merintis', range: '< 26', desc: 'TBM baru mulai membangun tata kelola dan program dasar.' },
];

export function TbmReportPdfModal({
  open,
  onClose,
  data,
  isPelita,
}: TbmReportPdfModalProps) {
  const printAreaRef = useRef<HTMLDivElement>(null);

  if (!open) return null;

  const { tbm, latest, dimensions, trend, bands } = data;
  if (!latest) return null;

  const handlePrint = () => {
    window.print();
  };

  const regionText = tbm.regionPath?.map((r) => r.name).join(' → ') || '—';

  // Standar definisi kategori dari instrumen atau fallback
  const bandList = bands && bands.length > 0
    ? bands.map((b) => ({
        name: b.name,
        range: b.display || (b.gt !== null ? `> ${b.gt}` : '≤ batas bawah'),
        desc: b.characteristic || DEFAULT_TBM_BANDS.find((d) => d.name.toLowerCase() === b.name.toLowerCase())?.desc || '—',
      }))
    : DEFAULT_TBM_BANDS;

  const currentBandDef = bandList.find(
    (b) => b.name.toLowerCase() === latest.band?.toLowerCase(),
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4 sm:p-6 backdrop-blur-xs print:static print:p-0 print:bg-white print:overflow-visible">
      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl overflow-hidden print:max-h-none print:shadow-none print:m-0 print:p-0 print:border-none print:w-full">
        {/* Modal Toolbar (hidden when printing) */}
        <div className="no-print flex items-center justify-between border-b border-border bg-subtle px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">🏫</span>
            <div>
              <h3 className="text-base font-bold text-ink">Pratinjau Cetak / PDF Rapor TBM</h3>
              <p className="text-xs text-ink-secondary">{tbm.code} · {tbm.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={onClose}>
              Tutup
            </Button>
            <Button onClick={handlePrint}>
              🖨️ Cetak / Simpan PDF
            </Button>
          </div>
        </div>

        {/* Printable Report Document */}
        <div
          ref={printAreaRef}
          className="printable-report flex-1 overflow-y-auto p-8 sm:p-12 text-ink print:overflow-visible print:p-0 print:text-black"
        >
          {/* Header Kop Resmi */}
          <div className="border-b-2 border-primary pb-5 mb-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <img src="/favicon.png" alt="Logo LENTERA" className="size-12 object-contain" />
                <div>
                  <h2 className="text-base font-bold tracking-tight text-ink uppercase">
                    Sekolah Literasi Indonesia
                  </h2>
                  <p className="text-xs text-ink-secondary">
                    LENTERA — Sistem Monitoring & Evaluasi Program Pendampingan TBM
                  </p>
                </div>
              </div>
              <div className="text-right text-xs text-ink-secondary">
                <span className="inline-block px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20 mb-1">
                  Dokumen Resmi
                </span>
                <p className="tabular">Dicetak: {new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-ink tracking-tight uppercase">
                  Rapor Indeks Keberdayaan TBM
                </h1>
                <p className="text-xs text-ink-secondary">
                  Periode Evaluasi: <strong className="text-ink">{latest.periodName}</strong>
                </p>
              </div>
              <div className="text-left sm:text-right">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                  <span className="size-1.5 rounded-full bg-emerald-600" />
                  Status: {latest.status.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {/* Bagian Identitas TBM */}
          <div className="rounded-lg border border-border bg-subtle/30 p-5 mb-6">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
              <div className="space-y-1.5 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-block px-2.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-primary text-white">
                    {tbm.code}
                  </span>
                  {tbm.angkatan && (
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                      Angkatan {tbm.angkatan}
                    </span>
                  )}
                </div>
                <h2 className="text-xl font-bold text-ink">{tbm.name}</h2>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-xs border-t border-border/80 pt-3 text-left">
              <div>
                <span className="text-ink-muted block text-[11px]">Wilayah:</span>
                <strong className="text-ink font-semibold">{regionText}</strong>
              </div>
              {tbm.address && (
                <div>
                  <span className="text-ink-muted block text-[11px]">Alamat Lengkap:</span>
                  <span className="text-ink">{tbm.address}</span>
                </div>
              )}
            </div>
          </div>

          {/* Hero Banner Capaian Level */}
          <div className="rounded-xl border-2 border-primary/30 bg-primary-soft/20 p-5 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-primary">
                  Indeks Keberdayaan TBM
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-4xl font-black text-ink tabular">
                    {latest.index !== null ? latest.index.toFixed(1) : '—'}
                  </span>
                  <span className="text-sm font-semibold text-ink-muted">/ 100</span>
                </div>
              </div>

              <div className="text-left sm:text-right">
                <span className="text-xs font-bold uppercase tracking-wider text-ink-secondary block mb-1">
                  Tahapan Keberdayaan
                </span>
                <span className="inline-block px-3.5 py-1.5 rounded-lg text-sm font-black uppercase tracking-wider bg-primary text-white shadow-xs">
                  {latest.band || 'Belum Ditetapkan'}
                </span>
              </div>
            </div>

            {currentBandDef && (
              <div className="mt-4 pt-3 border-t border-primary/20 text-xs text-ink-secondary leading-relaxed">
                <strong className="text-ink font-semibold">Definisi Tahapan: </strong>
                {currentBandDef.desc}
              </div>
            )}
          </div>

          {/* Tabel Rincian Dimensi */}
          <div className="mb-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-ink border-b border-border pb-1.5 mb-3">
              Rincian Capaian per Dimensi Keberdayaan
            </h3>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-xs text-left">
                <thead className="bg-subtle text-ink-secondary border-b border-border font-semibold">
                  <tr>
                    <th className="px-3 py-2 w-10 text-center">No</th>
                    <th className="px-3 py-2">Dimensi Keberdayaan</th>
                    <th className="px-3 py-2 w-20 text-center">Bobot</th>
                    <th className="px-3 py-2 w-24 text-right">Skor Nilai</th>
                    <th className="px-3 py-2 w-32">Pencapaian</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {dimensions.map((dim, idx) => {
                    const pct = Math.min(100, Math.max(0, dim.value));
                    return (
                      <tr key={dim.dimensionId} className="hover:bg-subtle/30">
                        <td className="px-3 py-2.5 text-center text-ink-muted font-medium">{idx + 1}</td>
                        <td className="px-3 py-2.5">
                          <strong className="font-semibold text-ink block">{dim.name}</strong>
                          <span className="text-[11px] text-ink-muted">{dim.code}</span>
                        </td>
                        <td className="px-3 py-2.5 text-center font-medium tabular text-ink-secondary">
                          {dim.weight}%
                        </td>
                        <td className="px-3 py-2.5 text-right font-bold text-ink tabular">
                          {dim.value.toFixed(1)}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-border overflow-hidden">
                              <div
                                className="h-full rounded-full bg-primary"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="w-9 text-right font-semibold tabular text-[11px] text-ink-secondary">
                              {pct.toFixed(0)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Riwayat Antar Semester (Tren) jika ada */}
          {trend.length > 1 && (
            <div className="mb-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-ink border-b border-border pb-1.5 mb-3">
                Riwayat Perkembangan Indeks Keberdayaan Antar Semester
              </h3>
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-xs text-left">
                  <thead className="bg-subtle text-ink-secondary border-b border-border font-semibold">
                    <tr>
                      <th className="px-3 py-2">Semester / Periode</th>
                      <th className="px-3 py-2 text-center">Tahun Program</th>
                      <th className="px-3 py-2 text-right">Indeks Keberdayaan</th>
                      <th className="px-3 py-2">Tahapan Capaian</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {trend.map((t, idx) => (
                      <tr key={idx} className={t.periodName === latest.periodName ? 'bg-primary-soft/20 font-medium' : ''}>
                        <td className="px-3 py-2">
                          {t.periodName}
                          {t.periodName === latest.periodName && (
                            <span className="ml-2 text-[10px] uppercase font-bold text-primary">(Saat Ini)</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center text-ink-secondary">
                          {t.programYear ? `Tahun ${t.programYear}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-bold tabular">
                          {t.index !== null ? t.index.toFixed(1) : '—'}
                        </td>
                        <td className="px-3 py-2 font-semibold text-primary">{t.band || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Standar Tahapan Keberdayaan TBM */}
          <div className="mb-8">
            <h3 className="text-xs font-bold uppercase tracking-wider text-ink border-b border-border pb-1.5 mb-3">
              Standar Definisi Tahapan Keberdayaan TBM
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {bandList.map((b) => {
                const isCurrent = b.name.toLowerCase() === latest.band?.toLowerCase();
                return (
                  <div
                    key={b.name}
                    className={`rounded-md p-2.5 border ${
                      isCurrent
                        ? 'border-primary bg-primary-soft/30 font-medium shadow-2xs'
                        : 'border-border bg-subtle/20 text-ink-secondary'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1 mb-1">
                      <span className={`font-bold text-xs ${isCurrent ? 'text-primary' : 'text-ink'}`}>
                        {b.name} {isCurrent && '★ (Posisi Saat Ini)'}
                      </span>
                      <span className="font-semibold text-ink-muted tabular">{b.range}</span>
                    </div>
                    <p className="text-[11px] leading-relaxed text-ink-secondary">{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
